import React, { useState, useEffect } from "react";
import {
  Box,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  InputAdornment,
  TextField,
  IconButton,
} from "@mui/material";
import { Search, Close, Person } from "@mui/icons-material";
import SchoolBusStatsCard from "./SchoolBusCard";
import SubTitle from "../common/SubTitle";
import { useNavigate } from "react-router-dom";
import SchoolDashboardMap from "./SchoolDashboardMap";
import OrganizationInactiveTrackingPanel from "./OrganizationInactiveTrackingPanel";
import axios from "axios";
import config from "../../config.js";
import PerformanceAnalytics from "./PerformanceAnalytics.jsx";
import { useUser } from "../../pages/login/UserContextPersistent";
import useDashboardMetrics from "../../hooks/useDashboardMetrics";
import { getWidgetIcon } from "../../config/widgetIconRegistry";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { useDashboardLayout } from "../../context/LayoutContext";
import schoolDashboardFallback from "../../layouts/fallback/school-dashboard.json";

// ─── Helpers ────────────────────────────────────────────────────────────────

const getOrgId = () => {
  try {
    return JSON.parse(localStorage.getItem("userState"))?.user?.organizationId;
  } catch {
    return null;
  }
};

const safeArray = (val) => (Array.isArray(val) ? val : []);

// ─── Component ───────────────────────────────────────────────────────────────

export default function SchoolDashboard({ dashboard = false }) {
  const navigate = useNavigate();
  const { state, organizationInactive } = useUser();
  const orgId = getOrgId();

  const [loading, setLoading] = useState(true);
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const { layout: jsonLayout } = useDashboardLayout(
    useJsonLayout ? PAGE_IDS.SCHOOL_DASHBOARD : null,
  );
  const layout = useJsonLayout ? jsonLayout : schoolDashboardFallback;
  const [mapStops, setMapStops] = useState([]);
  const [mapRoutes, setMapRoutes] = useState([]);
  const [mapVehicles, setMapVehicles] = useState([]);

  // Parent View Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStudents, setModalStudents] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const hasAnalyticsFeature = state?.features?.some(
    (feature) => feature.api_path === "/api/managedata/analytics",
  );

  useEffect(() => {
    if (organizationInactive) {
      setLoading(false);
      return;
    }
    fetchSchoolBusesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationInactive]);

  const { metrics, loading: metricsLoading } = useDashboardMetrics(layout, {
    organizationId: orgId,
  });

  // ── API ────────────────────────────────────────────────────────────────────

  const getRouteByStopCoordinates = async (lat, lng, stopOrgId) => {
    try {
      const { data } = await axios.get(`${config.baseURL}/route/coordinates/`, {
        params: { longitude: lng, latitude: lat, organizationId: stopOrgId },
        withCredentials: true,
      });
      return data;
    } catch (error) {
      console.error("Error fetching routes by coordinates:", error);
      return [];
    }
  };

  const fetchSchoolBusesData = async () => {
    try {
      setLoading(true);

      const [vehiclesRes, stopsRes, routesRes] = await Promise.all([
        axios.get(`${config.baseURL}/vehicle/organization`, {
          withCredentials: true,
        }),
        axios.get(`${config.baseURL}/stop/organization`, {
          withCredentials: true,
        }),
        axios.get(`${config.baseURL}/route/organization`, {
          params: { organizationId: orgId },
          withCredentials: true,
        }),
      ]);

      const vehicles = safeArray(vehiclesRes.data);
      const stops = safeArray(stopsRes.data);
      const routes = safeArray(routesRes.data);

      // Enrich stops with their routes
      const stopsWithRoutes = await Promise.all(
        stops.map(async (stop) => {
          const stopRoutes = await getRouteByStopCoordinates(
            stop.latitude,
            stop.longitude,
            stop.organizationId,
          );
          return { ...stop, routes: stopRoutes };
        }),
      );

      // Enrich routes with their stops
      const routesWithStops = routes.map((route) => {
        const routeStops = safeArray(route.routeCoordinates)
          .filter(
            (c) => c?.stopName && c.latitude != null && c.longitude != null,
          )
          .map((c) => ({
            id: c.id,
            stopId: c.id,
            name: c.stopName || "Unnamed Stop",
            latitude: parseFloat(c.latitude),
            longitude: parseFloat(c.longitude),
            order: c.order,
            routeId: route.id,
          }));
        return { ...route, stops: routeStops };
      });

      // Transform for map
      const transformedStops = stopsWithRoutes.map((stop) => ({
        id: stop.id,
        name: stop.name || stop.stopId,
        latitude: parseFloat(stop.latitude),
        longitude: parseFloat(stop.longitude),
        status: stop.status || "ACTIVE",
        stopId: stop.stopId,
        routes: stop.routes || [],
      }));

      const transformedRoutes = routesWithStops.map((route) => ({
        id: route.id,
        name: route.name,
        routeId: route.routeId,
        routeColor: route.routeColor || "#FF6B6B",
        routeCoordinates: route.routeCoordinates || [],
        stops: route.stops || [],
        vehicles: route.vehicles || [],
      }));

      const transformedVehicles = vehicles.map((vehicle) => ({
        id: vehicle.id,
        name: vehicle.name,
        VehicleNumber: vehicle.VehicleNumber,
        device_id: vehicle.device_id,
        routeId: vehicle.routeId || vehicle.route?.id || null,
        status: vehicle.status?.toUpperCase() || "ACTIVE",
        latitude: parseFloat(vehicle.latitude || 0),
        longitude: parseFloat(vehicle.longitude || 0),
        vehicle_color:
          vehicle.status === "ACTIVE"
            ? "green"
            : vehicle.status === "INACTIVE"
              ? "red"
              : "yellow",
        route: vehicle.route,
        driver: vehicle.driver,
        documents: vehicle.documents || [],
      }));

      setMapStops(transformedStops);
      setMapRoutes(transformedRoutes);
      setMapVehicles(transformedVehicles);
      // console.log("Fetched and transformed data:", {
      //   stops: transformedStops,
      //   routes: transformedRoutes,
      //   vehicles: transformedVehicles,
      // });
    } catch (error) {
      console.error("Error fetching school buses data:", error);
      if (error.response?.status === 401) navigate("/");
    } finally {
      setLoading(false);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleParentView = () => {
    navigate(`/parent-view?parentId=P00AR`);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSearchQuery("");
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const filteredStudents = modalStudents.filter((s) => {
    const fullName = `${s.firstName} ${s.lastName}`.toLowerCase();
    const q = searchQuery.toLowerCase();
    return fullName.includes(q) || s.studentId?.toLowerCase().includes(q);
  });

  const hasPermission = (requiredPath, requiredAction = null) => {
    if (!requiredPath || !state?.features) return true;
    return state.features.some((feature) => {
      const pathMatches = feature.api_path?.includes(requiredPath);
      if (!pathMatches) return false;
      if (!requiredAction) return true;
      return feature.action === requiredAction;
    });
  };

  const metricsSection = layout?.sections?.find((section) => section.type === "metricGrid");
  const cardWidgets = Array.isArray(metricsSection?.widgets)
    ? metricsSection.widgets.filter((widget) =>
        hasPermission(widget?.permission?.apiPath, widget?.permission?.action),
      )
    : [];

  const responsiveGridSx = {
    display: "flex",
    flexWrap: "wrap",
    gap: "24px",
    "& > *": {
      flex: "1 1 calc(16.666% - 20px)",
      minWidth: "180px",
      maxWidth: "100%",
    },
    "@media (max-width: 1600px)": {
      "& > *": { flex: "1 1 calc(20% - 19.2px)" },
    },
    "@media (max-width: 1200px)": { "& > *": { flex: "1 1 calc(25% - 18px)" } },
    "@media (max-width: 900px)": {
      "& > *": { flex: "1 1 calc(33.333% - 16px)" },
    },
    "@media (max-width: 600px)": {
      "& > *": { flex: "1 1 calc(33.333% - 16px)", minWidth: "100px" },
    },
  };

  // ── Loading skeleton ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <>
        <SubTitle
          title="Dashboard"
          path=""
          track="Parent View"
          onTrackClick={handleParentView}
        />
        <Box sx={{ margin: "0px 40px" }}>
          <Box sx={responsiveGridSx}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  p: 2,
                  borderRadius: "20px",
                  border: "1px solid #E3E7EE",
                  backgroundColor: "#FFFFFF",
                  minHeight: "140px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Skeleton variant="text" width="45%" height={28} />
                  <Skeleton variant="circular" width={44} height={44} />
                </Box>
                <Skeleton variant="text" width="35%" height={44} />
                <Skeleton variant="text" width="60%" height={24} />
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ margin: "14px 40px 0" }}>
          <Skeleton
            variant="rectangular"
            height={420}
            sx={{ borderRadius: "24px" }}
          />
        </Box>
      </>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <SubTitle
        title="Dashboard"
        path=""
        track={!organizationInactive ? "Parent View" : ""}
        onTrackClick={!organizationInactive ? handleParentView : null}
      />

      {/* Stats Cards */}
      <Box sx={{ margin: "0px 40px" }}>
        <Box sx={responsiveGridSx}>
          {cardWidgets.map((widget) => (
            <SchoolBusStatsCard
              key={widget.id}
              title={widget.labelKey}
              number={metrics[widget.metricKey] ?? "--"}
              icon={getWidgetIcon(widget.icon, widget.labelKey)}
              extraInfo={[]}
              color={widget.color}
              onClick={() => widget.navigateTo && navigate(widget.navigateTo)}
            />
          ))}
        </Box>
      </Box>

      {/* Map */}
      {organizationInactive ? (
        <OrganizationInactiveTrackingPanel />
      ) : (
        <Box sx={{ marginTop: "-10px" }}>
          <SchoolDashboardMap
            stops={mapStops}
            routes={mapRoutes}
            vehicles={mapVehicles}
          />
        </Box>
      )}

      {/* Performance Analytics — only when dashboard prop is true and feature is enabled */}
      {!organizationInactive && dashboard && hasAnalyticsFeature && (
        <Box sx={{ mt: 4 }}>
          <PerformanceAnalytics vehiclesData={mapVehicles} />
        </Box>
      )}

      {/* Parent View — Student Selection Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            width: "440px",
            maxWidth: "95vw",
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            borderBottom: "1px solid #F0F0F0",
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: "16px", color: "#1A1A2E" }}
            >
              Select a Student
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#8A8A9A", mt: 0.3 }}>
              Click a student to view their parent's tracking page
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleModalClose}
            sx={{ color: "#8A8A9A" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or student ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "#8A8A9A" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: "10px", fontSize: "13px" },
            }}
          />
        </Box>

        <DialogContent sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
          {modalLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 5,
              }}
            >
              <CircularProgress size={32} sx={{ color: "#1A73E8" }} />
            </Box>
          ) : filteredStudents.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Person sx={{ fontSize: 40, color: "#D0D0D0", mb: 1 }} />
              <Typography sx={{ fontSize: "13px", color: "#8A8A9A" }}>
                {searchQuery
                  ? "No students match your search"
                  : "No students found"}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredStudents.map((student) => (
                <ListItem key={student.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      handleModalClose();
                      navigate(
                        `/parent-view?parentId=${student.parent.parentId}`,
                      );
                    }}
                    sx={{
                      borderRadius: "10px",
                      border: "1.5px solid transparent",
                      "&:hover": {
                        backgroundColor: "#F7F8FA",
                        borderColor: "#E0E0E0",
                      },
                      transition: "all 0.15s ease",
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: "14px",
                          fontWeight: 700,
                          backgroundColor: "#E8EDF5",
                          color: "#5A6A85",
                        }}
                      >
                        {student.firstName?.[0]?.toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "13.5px",
                            color: "#1A1A2E",
                          }}
                        >
                          {student.firstName} {student.lastName}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          sx={{ fontSize: "11.5px", color: "#8A8A9A", mt: 0.1 }}
                        >
                          {student.studentId}&nbsp;·&nbsp;Grade {student.grade}
                          {student.section ? ` - ${student.section}` : ""}
                          &nbsp;·&nbsp;{student.route?.name || "No Route"}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
